/*
 * Boston Proper Shop-the-Look drawer — global handler.
 *
 * Loaded once from layout/theme.liquid. Wires up every dialog with class
 * "bp-stl-drawer" on the page: open via [data-stl-trigger][data-drawer-id],
 * close via backdrop / X / Esc, variant selects, single Add-to-bag, and
 * batch "Add all to bag". Shared by bp-shop-the-look (homepage) and
 * bp-collection (PLP chip tiles in shop_the_look mode).
 *
 * Triggers and close buttons use document-level event delegation so they
 * work for drawers added at any time (theme editor reloads, PLP load-
 * more, etc.) without needing re-init. Per-dialog binding (variant
 * pickers, add-to-cart forms) is idempotent via dataset.bpStlBound.
 */
(function () {
  'use strict';

  // ---- Delegated click handler — covers triggers and close buttons ----
  document.addEventListener('click', (e) => {
    // Open: [data-stl-trigger]
    const trigger = e.target.closest('[data-stl-trigger]');
    if (trigger) {
      e.preventDefault();
      const id = trigger.dataset.drawerId;
      if (!id) return;
      const dialog = document.getElementById('bp-stl-drawer-' + id);
      if (!dialog) return;
      // Reusable shell (the PLP Bloomreach content drawer): one dialog
      // serves every BR "shop the look" tile, so the look is carried on the
      // TRIGGER, not baked into the dialog. Transfer style/collection/title
      // and reset the populated flag when they change so the drawer
      // re-fetches for the newly clicked look.
      if (dialog.dataset.bpStlReusable === '1') {
        const tStyle = trigger.dataset.bpStlStyle || '';
        const tColl = trigger.dataset.bpStlCollection || '';
        const prevStyle = dialog.getAttribute('data-bp-stl-style') || '';
        const prevColl = dialog.getAttribute('data-bp-stl-collection') || '';
        if (tStyle !== prevStyle || tColl !== prevColl) {
          if (tStyle) dialog.setAttribute('data-bp-stl-style', tStyle);
          else dialog.removeAttribute('data-bp-stl-style');
          if (tColl) dialog.setAttribute('data-bp-stl-collection', tColl);
          else dialog.removeAttribute('data-bp-stl-collection');
          delete dialog.dataset.bpStlPopulated;
        }
        const tTitle = trigger.dataset.bpStlTitle;
        if (tTitle) {
          const titleEl = dialog.querySelector('.bp-stl-drawer__title');
          if (titleEl) titleEl.textContent = tTitle;
        }
      }
      // Guard against double-open (the bp-shop-the-look section also wires
      // its CTAs): showModal() throws if the dialog is already open.
      if (!dialog.open) {
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
      }
      populateFromStylitics(dialog);
      return;
    }

    // Close: [data-stl-close] inside a drawer
    const closer = e.target.closest('[data-stl-close]');
    if (closer) {
      const dialog = closer.closest('.bp-stl-drawer');
      if (dialog) closeDrawer(dialog);
      return;
    }

    // Backdrop click on the dialog itself (target IS the dialog,
    // not its content)
    if (e.target.classList && e.target.classList.contains('bp-stl-drawer')) {
      closeDrawer(e.target);
    }
  });

  // ---- Per-dialog wiring for forms / add-all (idempotent) ----
  function bindDialogs(scope) {
    (scope || document).querySelectorAll('.bp-stl-drawer').forEach((dialog) => {
      if (dialog.dataset.bpStlBound) return;
      dialog.dataset.bpStlBound = '1';

      dialog.querySelectorAll('.bp-stl-drawer__product').forEach((form) => {
        wireVariantSelection(form);
        wireAddToCart(form);
      });
      dialog.querySelectorAll('[data-stl-add-all]').forEach((btn) => {
        btn.addEventListener('click', () => addAllToBag(dialog));
      });
    });
  }

  function closeDrawer(dialog) {
    try {
      if (typeof dialog.close === 'function') {
        dialog.classList.add('bp-stl-drawer--closing');
        setTimeout(() => {
          dialog.classList.remove('bp-stl-drawer--closing');
          dialog.close();
        }, 180);
      }
    } catch (_) { dialog.removeAttribute('open'); }
  }

  function wireVariantSelection(form) {
    let variants = [];
    try { variants = JSON.parse(form.dataset.variants); } catch (_) {}
    const selects = form.querySelectorAll('.bp-stl-drawer__option-select');
    const variantInput = form.querySelector('[data-variant-id]');
    const submitBtn = form.querySelector('[data-stl-add]');
    const labelEl = form.querySelector('.bp-stl-drawer__add-label');
    const refresh = () => {
      const chosen = Array.from(selects).map((s) => s.value);
      // Any option still on its "Select …" placeholder → no variant, no add.
      const unset = Array.from(selects).find((s) => !s.value);
      if (unset) {
        if (variantInput) variantInput.value = '';
        if (submitBtn) submitBtn.disabled = true;
        if (labelEl) labelEl.textContent = 'Select ' + (unset.dataset.bpOptionName || 'Size');
        return;
      }
      const match = variants.find((v) => {
        const opts = v.options || [];
        return chosen.every((val, i) => opts[i] === val);
      });
      if (match) {
        if (variantInput) variantInput.value = match.id;
        if (submitBtn) submitBtn.disabled = !match.available;
        if (labelEl) labelEl.textContent = match.available ? 'Add to bag' : 'Sold out';
      } else {
        if (variantInput) variantInput.value = '';
        if (submitBtn) submitBtn.disabled = true;
        if (labelEl) labelEl.textContent = 'Unavailable';
      }
    };
    selects.forEach((s) => s.addEventListener('change', () => {
      form.classList.remove('bp-stl-drawer__product--needs-select');
      refresh();
    }));
    refresh(); // set the initial disabled / "Select Size" state
  }

  function wireAddToCart(form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('[data-stl-add]');
      const labelEl = form.querySelector('.bp-stl-drawer__add-label');
      const variantId = form.querySelector('[data-variant-id]')?.value;
      if (!variantId) return;
      if (btn) btn.disabled = true;
      try {
        // Attach the SAME base metafield LIPs as the PDP / quick-view add
        // (via bpLips.forVariant), and nothing else — so a Shop-the-Look add
        // of a product MERGES into an existing PDP line for the same variant
        // instead of creating a second identical line (QA 7/24, Dragana).
        // It deliberately does NOT stamp the outfit-bundle upsell flags
        // (_upsell_type/_upsell_area/_is_bundle_product): those aren't on the
        // PDP form's add, so their presence would make the property sets
        // differ and block Shopify's line merge. /cart/add.js auto-merges a
        // line with an identical variant + properties. (The upsell flags stay
        // on the actual discounted "Complete the Look" bundle feature.)
        let res;
        if (window.bpLips && window.bpLips.forVariant) {
          const props = await window.bpLips.forVariant(formHandle(form), variantId).catch(() => ({}));
          const body = { id: variantId, quantity: 1 };
          if (props && Object.keys(props).length) body.properties = props;
          res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
        } else {
          res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 }),
          });
        }
        if (!res || !res.ok) throw new Error('Add failed');
        if (labelEl) labelEl.textContent = 'Added';
        if (btn) btn.dataset.state = 'added';
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        setTimeout(() => {
          if (btn) { delete btn.dataset.state; btn.disabled = false; }
          if (labelEl) labelEl.textContent = 'Add to bag';
        }, 1500);
      } catch (err) {
        if (btn) btn.disabled = false;
        if (labelEl) labelEl.textContent = 'Try again';
      }
    });
  }

  async function addAllToBag(dialog) {
    const btn = dialog.querySelector('[data-stl-add-all]');
    const forms = Array.from(dialog.querySelectorAll('.bp-stl-drawer__product'));
    // Every look item must have an actively chosen variant — never add a
    // defaulted size. Flag any item still on "Select Size" and stop.
    forms.forEach((f) => f.classList.remove('bp-stl-drawer__product--needs-select'));
    const missing = forms.filter((f) => !(f.querySelector('[data-variant-id]') || {}).value);
    if (missing.length) {
      missing.forEach((f) => f.classList.add('bp-stl-drawer__product--needs-select'));
      try { missing[0].scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) {}
      const firstSel = missing[0].querySelector('[data-bp-requires-select]');
      if (firstSel) { try { firstSel.focus(); } catch (_) {} }
      return;
    }
    const items = (await Promise.all(forms.map(async (f) => {
      const id = f.querySelector('[data-variant-id]')?.value;
      if (!id) return null;
      const item = { id: parseInt(id, 10), quantity: 1 };
      try {
        // Same LIPs as a single add — this batch path also bypasses the
        // form. Base metafield LIPs ONLY (no outfit-bundle upsell flags), so
        // each item merges with an existing PDP line for the same variant
        // rather than creating a duplicate (QA 7/24, Dragana).
        if (window.bpLips) {
          const base = await window.bpLips.forVariant(formHandle(f), id);
          if (base && Object.keys(base).length) item.properties = base;
        }
      } catch (_) { /* add without props rather than fail the whole batch */ }
      return item;
    }))).filter(Boolean);
    if (items.length === 0) return;
    if (btn) btn.disabled = true;
    const origLabel = btn?.textContent;
    if (btn) btn.textContent = 'Adding…';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('Add-all failed');
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      if (btn) btn.textContent = 'Added!';
      setTimeout(() => {
        closeDrawer(dialog);
        if (btn) { btn.textContent = origLabel; btn.disabled = false; }
      }, 800);
    } catch (err) {
      if (btn) { btn.textContent = origLabel; btn.disabled = false; }
    }
  }

  // ---- Stylitics population (real "shop the look" outfits) ----
  // When a drawer carries data-bp-stl-style (the look's anchor bp_style),
  // fetch a real outfit from Stylitics' public widget API — the same
  // endpoint the PDP/cart Complete-the-Look uses — resolve each outfit
  // item to a Shopify product (so it has variants + an add-to-bag path),
  // and replace the drawer body with those product forms. If anything
  // returns empty, we leave the server-rendered collection fallback.
  const STL_ENDPOINT = 'https://widget-api.stylitics.com/api/outfits';
  const STL_USERNAME = 'bostonproper';
  const STL_MAX = 8;

  function escAttr(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }
  function money(cents) {
    if (cents == null || isNaN(cents)) return '';
    return '$' + (cents / 100).toFixed(2);
  }
  function sizeImg(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
  }
  function handleFromUrl(url) {
    const m = String(url || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  // Product handle for a drawer form — used to fetch its LIPs (?view=lips).
  // Server-rendered rows (bp-stl-drawer-row.liquid) and JS-built forms both
  // carry data-handle now; fall back to the product link href just in case.
  function formHandle(form) {
    if (form && form.dataset && form.dataset.handle) return form.dataset.handle;
    const link = form && form.querySelector('.bp-stl-drawer__product-title, .bp-stl-drawer__product-image');
    return handleFromUrl(link && link.getAttribute('href')) || '';
  }

  // Build a drawer product form from a /products/{handle}.js payload,
  // matching snippets/bp-stl-drawer.liquid's markup so the shared CSS +
  // variant/add wiring apply unchanged.
  function buildProductForm(product) {
    const variants = product.variants || [];
    const initial = variants.find((v) => v.available) || variants[0];
    if (!initial) return null;
    const url = product.url || ('/products/' + product.handle);
    const optionNames = (product.options || []).map((o) =>
      typeof o === 'string' ? o : (o && o.name) || ''
    );

    const form = document.createElement('form');
    form.className = 'bp-stl-drawer__product';
    form.dataset.productId = product.id;
    form.dataset.handle = product.handle || '';
    form.dataset.variants = JSON.stringify(variants);

    let needsSelect = false;
    let selectLabel = 'Select Size';
    let optsHtml = '';
    optionNames.forEach((name, i) => {
      const values = [];
      variants.forEach((v) => {
        const val = (v.options || [])[i];
        if (val != null && values.indexOf(val) === -1) values.push(val);
      });
      if (!values.length) return;
      // Multi-value options must be actively chosen — no default (prevents
      // accidental first-size / XXS adds). Single-value options auto-select.
      const isMulti = values.length > 1;
      if (isMulti && !needsSelect) { needsSelect = true; selectLabel = 'Select ' + name; }
      const selected = (initial.options || [])[i];
      optsHtml +=
        '<label class="bp-stl-drawer__option"><span class="visually-hidden">' + escAttr(name) + '</span>' +
        '<select class="bp-stl-drawer__option-select" data-option-position="' + (i + 1) + '" data-bp-option-name="' + escAttr(name) + '" name="options[' + escAttr(name) + ']"' + (isMulti ? ' data-bp-requires-select' : '') + '>' +
        (isMulti ? '<option value="" disabled selected>Select ' + escAttr(name) + '</option>' : '') +
        values.map((val) => {
          // OOS grey-out (QA 7/21): disable a value with no in-stock variant
          // so sold-out sizes render greyed + unpickable instead of only
          // revealing "Sold out" after selection.
          const isAvail = variants.some((v) => v.available && (v.options || [])[i] === val);
          return '<option value="' + escAttr(val) + '"' +
            (!isMulti && val === selected ? ' selected' : '') +
            (isAvail ? '' : ' disabled data-bp-oos="1"') + '>' +
            escAttr(name) + ': ' + escAttr(val) + (isAvail ? '' : ' — Sold out') + '</option>';
        }).join('') +
        '</select></label>';
    });

    const compare = initial.compare_at_price && initial.compare_at_price > initial.price
      ? '<s class="bp-stl-drawer__compare">' + money(initial.compare_at_price) + '</s>'
      : '';
    // Prefer the 2nd image (the "ghost" / flat product shot) over the
    // lifestyle/featured shot — matches snippets/bp-stl-drawer-row's
    // Liquid pick. Falls back to featured_image when the product has
    // only one image. /products/{handle}.js returns string URLs in
    // .images and the featured_image as a string too.
    const ghostUrl = Array.isArray(product.images) && product.images.length > 1
      ? product.images[1]
      : null;
    const thumbUrl = ghostUrl || product.featured_image;
    const img = thumbUrl ? sizeImg(thumbUrl, 240) : '';

    form.innerHTML =
      '<a class="bp-stl-drawer__product-image" href="' + escAttr(url) + '" tabindex="-1" aria-hidden="true">' +
        (img ? '<img src="' + escAttr(img) + '" width="120" height="150" alt="" loading="lazy">' : '') +
      '</a>' +
      '<div class="bp-stl-drawer__product-body">' +
        '<a class="bp-stl-drawer__product-title" href="' + escAttr(url) + '">' + escAttr(product.title) + '</a>' +
        '<div class="bp-stl-drawer__product-prices"><span class="bp-stl-drawer__price">' + money(initial.price) + '</span>' + compare + '</div>' +
        optsHtml +
        '<input type="hidden" name="id" value="' + (needsSelect ? '' : escAttr(initial.id)) + '" data-variant-id>' +
        '<input type="hidden" name="quantity" value="1">' +
        '<button type="submit" class="bp-stl-drawer__add" data-stl-add' + ((needsSelect || !initial.available) ? ' disabled' : '') + '>' +
          '<span class="bp-stl-drawer__add-label">' + (needsSelect ? escAttr(selectLabel) : (initial.available ? 'Add to bag' : 'Sold out')) + '</span>' +
        '</button>' +
      '</div>';
    return form;
  }

  // Resolve a Stylitics look (by anchor bp_style) to a list of drawer
  // product forms. Empty array = no outfit / nothing resolved.
  async function styliticsForms(style) {
    let outfits = [];
    try {
      const res = await fetch(
        STL_ENDPOINT + '?username=' + encodeURIComponent(STL_USERNAME) + '&item_number=' + encodeURIComponent(style),
        { cache: 'force-cache' }
      );
      if (res.ok) {
        const data = await res.json();
        outfits = Array.isArray(data) ? data : [];
      }
    } catch (err) {
      console.warn('[bp-stl-drawer] Stylitics fetch failed', err);
    }
    // The first outfit is the cohesive look; keep its item order.
    let items = (outfits[0] && outfits[0].items) || [];
    if (!items.length) return [];
    items = items.slice(0, STL_MAX);

    const forms = [];
    const seen = {};
    for (let i = 0; i < items.length; i += 1) {
      const handle = handleFromUrl(items[i] && items[i].affiliate_link);
      if (!handle || seen[handle]) continue;
      seen[handle] = true;
      try {
        const r = await fetch('/products/' + handle + '.js', { cache: 'force-cache' });
        if (!r.ok) continue;
        const product = await r.json();
        const form = buildProductForm(product);
        if (form) forms.push(form);
      } catch (err) { /* skip unresolved item */ }
    }
    return forms;
  }

  // Fallback for BR content STL tiles that carry a collectionHandle: pull
  // the collection's product handles (built-in AJAX endpoint), then resolve
  // each via /products/{handle}.js so buildProductForm applies unchanged.
  async function collectionForms(handle) {
    const forms = [];
    let products = [];
    try {
      const r = await fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + STL_MAX, { cache: 'force-cache' });
      if (!r.ok) return forms;
      const data = await r.json();
      products = (data && data.products) || [];
    } catch (err) { return forms; }
    for (let i = 0; i < products.length; i += 1) {
      const h = products[i] && products[i].handle;
      if (!h) continue;
      try {
        const pr = await fetch('/products/' + h + '.js', { cache: 'force-cache' });
        if (!pr.ok) continue;
        const product = await pr.json();
        const form = buildProductForm(product);
        if (form) forms.push(form);
      } catch (err) { /* skip */ }
    }
    return forms;
  }

  async function populateFromStylitics(dialog) {
    const style = dialog.getAttribute('data-bp-stl-style');
    const collection = dialog.getAttribute('data-bp-stl-collection');
    if ((!style && !collection) || dialog.dataset.bpStlPopulated) return;
    dialog.dataset.bpStlPopulated = '1';

    const body = dialog.querySelector('.bp-stl-drawer__body');
    const addAll = dialog.querySelector('[data-stl-add-all]');
    if (!body) return;

    // The reusable BR shell has no server-rendered fallback rows, so show a
    // loading line while we fetch (server-rendered drawers keep their rows).
    const isReusable = dialog.dataset.bpStlReusable === '1';
    if (isReusable) body.innerHTML = '<p class="bp-stl-drawer__empty">Loading the look…</p>';

    let forms = [];
    if (style) forms = await styliticsForms(style);
    if (!forms.length && collection) forms = await collectionForms(collection);

    if (!forms.length) {
      // Non-reusable: leave the server-rendered collection fallback.
      // Reusable: nothing resolved — swap the loading line for a message.
      if (isReusable) body.innerHTML = '<p class="bp-stl-drawer__empty">No look available right now.</p>';
      return;
    }

    body.innerHTML = '';
    forms.forEach((form) => {
      body.appendChild(form);
      wireVariantSelection(form);
      wireAddToCart(form);
    });
    if (addAll) addAll.disabled = false;
  }

  // Public API for sections that morph DOM (PLP load-more) to wire new
  // drawers' forms.
  window.bpStlDrawer = { init: bindDialogs };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bindDialogs(), { once: true });
  } else {
    bindDialogs();
  }
})();
